import React, { useState, useEffect } from 'react';
import { Heart, X, Upload, Plus, LogOut, Loader, Trophy, Menu, User } from 'lucide-react';
import Leaderboard from './components/Leaderboard';
import SideLeaderboard from './components/SideLeaderboard';
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { getDatabase, ref, push, onValue, update, get } from "firebase/database";
import './styles.css';
import { createUserProfile } from './utils/userProfile';
import { validateImageFile, validateImageDimensions, compressImage } from './utils/imageValidation';
import UserProfile from './components/UserProfile';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import PricingModal from './components/PricingModal';
import SubscriptionManager from './components/SubscriptionManager';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import { trackUserUpload, canUserUploadToday, getRemainingUploads, hasPremiumFeatures } from './utils/subscriptionHelpers';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAVfD1EtOgGiaY3SjJzgdjSZWtNUbmQG9Y",
  authDomain: "onlybeanz-7e433.firebaseapp.com",
  projectId: "onlybeanz-7e433",
  storageBucket: "onlybeanz-7e433.firebasestorage.app",
  messagingSenderId: "904591320827",
  appId: "1:904591320827:web:8ad83af18af53afa91325e"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getDatabase(app);

// CLOUDINARY CONFIGURATION
const CLOUDINARY_CLOUD_NAME = "dxffceo2w";
const CLOUDINARY_UPLOAD_PRESET = "onlybeanz_uploads";

const BEAN_CATEGORIES = ["Pink Paws", "Black Beans", "Spotted Beans", "Multi-colored", "Fuzzy Beans"];

const OnlyBeanzApp = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [beans, setBeans] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [showAddBean, setShowAddBean] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [remainingUploads, setRemainingUploads] = useState(3);

  // Check for subscription success on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      setShowSubscriptionSuccess(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // New bean form state
  const [newBean, setNewBean] = useState({
    title: '',
    imageFile: null,
    imagePreview: '',
    imageUrl: '',
    category: BEAN_CATEGORIES[0] // Default category
  });

  // Function to add initial beans to Firestore (run once)
  // eslint-disable-next-line no-unused-vars
  const addInitialBeans = async () => {
    try {
      console.log("Inside addInitialBeans function.");
      const initialBeans = [
        {
          title: "Charlie",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530935/Unknown_irqmpu.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Pink Paws"
        },
        {
          title: "Max",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530934/Unknown-13_wdxesf.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Black Beans"
        },
        {
          title: "Milo",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530933/Unknown-12_l36rno.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Spotted Beans"
        },
        {
          title: "Sam",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530932/Unknown-11_styvpe.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Multi-colored"
        },
        {
          title: "Oliver",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530932/Unknown-10_fbp7yw.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Fuzzy Beans"
        },
        {
          title: "Riley",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530931/Unknown-9_awmktm.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Pink Paws"
        },
        {
          title: "Oscar",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530930/Unknown-8_oi0goa.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Black Beans"
        },
        {
          title: "Bailey",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530930/Unknown-7_whhvuh.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Spotted Beans"
        },
        {
          title: "Dakota",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530929/Unknown-6_zg3kcq.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Multi-colored"
        },
        {
          title: "Scout",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530928/Unknown-5_egwjvb.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Fuzzy Beans"
        },
        {
          title: "Bailey",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530928/Unknown-4_gmaidw.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Pink Paws"
        },
        {
          title: "Alex",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530927/Unknown-3_cayyvd.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Black Beans"
        },
        {
          title: "Sam",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530927/Unknown-2_vekbao.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Spotted Beans"
        },
        {
          title: "Piper",
          image: "https://res.cloudinary.com/dxffceo2w/image/upload/v1762530926/Unknown-1_kuhtte.jpg",
          ownerId: "initial-data",
          createdAt: new Date().toISOString(),
          likes: 0,
          category: "Multi-colored"
        }
      ];

      for (const bean of initialBeans) {
        await push(ref(db, "beans"), bean);
      }
      console.log("Initial beans successfully added to Realtime Database!");
    } catch (error) {
      console.error("Error in addInitialBeans:", error);
    }
  };


  // Listen for Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Create/update user profile when they sign in
        await createUserProfile(currentUser);
        // Check remaining uploads for today
        const remaining = await getRemainingUploads(currentUser);
        setRemainingUploads(remaining);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const currentBean = beans[currentIndex];

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();
      console.log('Cloudinary upload response:', data);
      
      if (data.secure_url) {
        return data.secure_url;
      }
 else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      throw error;
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      alert('Sign up successful! You are now logged in.');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      alert('Login successful!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      alert('Logged out successfully!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAnonymousLogin = async () => {
    try {
      await signInAnonymously(auth);
      alert('Signed in anonymously!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const fileValidation = validateImageFile(file);
    if (!fileValidation.isValid) {
      alert('Image validation failed:\n' + fileValidation.errors.join('\n'));
      return;
    }

    // Validate dimensions
    const dimensionValidation = await validateImageDimensions(file);
    if (!dimensionValidation.isValid) {
      alert('Image dimension validation failed:\n' + dimensionValidation.errors.join('\n'));
      return;
    }

    try {
      // Compress image if it's large
      let processedFile = file;
      if (file.size > 2 * 1024 * 1024) { // If larger than 2MB, compress
        console.log('Compressing image...');
        processedFile = await compressImage(file);
        console.log(`Image compressed from ${file.size} to ${processedFile.size} bytes`);
      }

      setNewBean({
        ...newBean,
        imageFile: processedFile,
        imagePreview: URL.createObjectURL(processedFile)
      });
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Failed to process image. Please try a different image.');
    }
  };

    const handleAddBean = async (e) => {
      e.preventDefault();
      
      if (!newBean.imageFile) {
        alert('Please select a beanz photo');
        return;
      }

      // Gentle reminder for support after multiple uploads
      const today = new Date().toDateString();
      const uploadsToday = await getRemainingUploads(user);
      if (uploadsToday >= 5) {
        // Show support modal occasionally for heavy users
        if (Math.random() < 0.3) { // 30% chance
          setShowPricing(true);
          // But still allow the upload
        }
      }
  
      setUploading(true);
  
      try {
        // Upload image to Cloudinary
        const imageUrl = await uploadToCloudinary(newBean.imageFile);
        
        // Create bean profile
        const beanData = {
          title: newBean.title,
          image: imageUrl,
          ownerId: user?.uid || 'demo-user',
          createdAt: new Date().toISOString(),
          likes: 0,
          category: newBean.category
        };

        console.log('Bean data to be added to Realtime Database:', beanData);
        await push(ref(db, 'beans'), beanData);
        console.log('Bean added to Realtime Database successfully!');

        // Track the upload for daily limits
        await trackUserUpload(user.uid);
        
        // Update remaining uploads
        const remaining = await getRemainingUploads(user);
        setRemainingUploads(remaining);
        
        alert('Beanz uploaded successfully! 🐾');
        
        setShowAddBean(false);
        
        // Reset form
        setNewBean({
          title: '',
          imageFile: null,
          imagePreview: '',
          imageUrl: ''
        });
        
      } catch (error) {
        console.error('Error uploading bean profile:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
      }
    };
  const handleSwipe = async (direction) => {
    const beanBeingRated = currentBean; // Capture the current bean before any state changes
    setSwipeDirection(direction);
    
    // Calculate next index immediately
    const nextIndex = currentIndex < beans.length - 1 ? currentIndex + 1 : 0;
    
    setTimeout(async () => {
      // Handle like logic in background without affecting UI progression
      if (direction === 'right' && beanBeingRated && user) {
        try {
          // Check if user has already liked this bean
          const userLikeRef = ref(db, `userLikes/${user.uid}/${beanBeingRated.id}`);
          const userLikeSnapshot = await get(userLikeRef);
          
          if (!userLikeSnapshot.exists()) {
            // User hasn't liked this bean yet
            const beanRef = ref(db, 'beans/' + beanBeingRated.id);
            
            // Update both the bean's total likes and user's like status
            await update(beanRef, {
              likes: beanBeingRated.likes + 1
            });
            
            await update(ref(db, `userLikes/${user.uid}`), {
              [beanBeingRated.id]: true
            });
            
            console.log(`User ${user.uid} liked bean ${beanBeingRated.id} (${beanBeingRated.title})`);
          } else {
            console.log(`User ${user.uid} has already liked bean ${beanBeingRated.id} (${beanBeingRated.title})`);
          }
        } catch (error) {
          console.error('Error handling like:', error);
        }
      }
      
      // Always move to next bean
      setCurrentIndex(nextIndex);
      setSwipeDirection(null);
    }, 300);
  };

  // Load beans from Realtime Database
  useEffect(() => {
    if (!user) {
      setBeans([]);
      setLoading(false);
      return;
    }

    console.log('Setting up Firebase listener for user:', user.uid);
    const beansRef = ref(db, 'beans');
    
    const unsubscribe = onValue(beansRef, async (snapshot) => {
      try {
        console.log('Firebase snapshot received:', snapshot.exists());
        
        const data = snapshot.val();
        const fetchedBeans = [];
        
        if (data) {
          for (let id in data) {
            fetchedBeans.push({ id, ...data[id] });
          }
          console.log('Fetched beans from Realtime Database:', fetchedBeans.length, 'beans');
        } else {
          console.log('No beans found in database, adding initial beans...');
          try {
            await addInitialBeans();
            return; // Exit early, the listener will be triggered again with new data
          } catch (error) {
            console.error('Error adding initial beans:', error);
            // Set empty array and stop loading to prevent infinite loading
            setBeans([]);
            setLoading(false);
            return;
          }
        }

        const filteredBeans = selectedCategory
          ? fetchedBeans.filter(bean => bean.category === selectedCategory)
          : fetchedBeans;

        setBeans(filteredBeans);
        setCurrentIndex(0);
        setLoading(false); // Stop loading once beans are loaded
      } catch (error) {
        console.error('Error in onValue callback:', error);
        setLoading(false);
        setBeans([]);
      }
    }, (error) => {
      console.error('Firebase onValue error:', error);
      setLoading(false);
      setBeans([]);
    });

    return () => unsubscribe();
  }, [user, selectedCategory]);

  // Login/Signup screen
  if (!user) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h1>
              OnlyBeanz
            </h1>
            
          </div>

          <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
            <div>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              className="button"
            >
              {isSignUp ? 'Sign Up' : 'Log In'}
            </button>
          </form>

          <div className="form-footer">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
            >
              {isSignUp ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
            </button>
          </div>

          <div className="form-footer">
            <button
              onClick={handleAnonymousLogin}
            >
              Continue as Guest
            </button>
          </div>


        </div>
      </div>
    );
  }

  // Add bean form
  if (showAddBean) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h2>Add New Beanz</h2>
            <button 
              onClick={() => setShowAddBean(false)}
              disabled={uploading}
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleAddBean}>
            <div>
              <label>
                Beanz Photo *
              </label>
              <div className="upload-area">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="bean-photo"
                  disabled={uploading}
                />
                <label htmlFor="bean-photo">
                  {newBean.imagePreview ? (
                    <img src={newBean.imagePreview} alt="Preview" />
                  ) : (
                    <div>
                      <Upload />
                      <p>Click to upload photo</p>
                      <p>Max 10MB</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div>
              <label>Title *</label>
              <input
                type="text"
                value={newBean.title}
                onChange={(e) => setNewBean({...newBean, title: e.target.value})}
                disabled={uploading}
                required
              />
            </div>

            <div>
              <label>Category *</label>
              <select
                value={newBean.category}
                onChange={(e) => setNewBean({...newBean, category: e.target.value})}
                disabled={uploading}
                required
              >
                {BEAN_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="button"
            >
              {uploading ? (
                <>
                  <Loader />
                  Uploading to Cloudinary...
                </>
              ) : (
                'Add Beanz'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }



if (showLeaderboard) {
    return <Leaderboard onClose={() => setShowLeaderboard(false)} selectedCategory={selectedCategory} />;
  }

  // User profile screen
  if (showUserProfile) {
    return <UserProfile user={user} onClose={() => setShowUserProfile(false)} />;
  }

  // Support modal (Buy Me a Coffee)
  if (showPricing) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h2>☕ Support OnlyBeanz</h2>
            <button onClick={() => setShowPricing(false)}>✕</button>
          </div>
          <div className="support-content">
            <h3>🐾 Love the toe beans?</h3>
            <p>OnlyBeanz is a passion project made with love for cat enthusiasts! Your support helps keep the app running and toe beans flowing.</p>
            
            <div className="support-options">
              <a 
                href="https://www.buymeacoffee.com/onlybeanz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="coffee-button"
              >
                ☕ Buy me a coffee!
              </a>
              
              <p className="support-text">
                Every donation, no matter how small, makes a huge difference! 💖
              </p>
            </div>
            
            <div className="thank-you">
              <p><strong>What your support does:</strong></p>
              <ul>
                <li>🔥 Keeps OnlyBeanz running smoothly</li>
                <li>✨ Helps add new features and categories</li>
                <li>🐾 Supports ongoing toe bean research!</li>
                <li>💝 Shows love for indie cat projects</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Subscription success screen
  if (showSubscriptionSuccess) {
    return <SubscriptionSuccess onContinue={() => setShowSubscriptionSuccess(false)} />;
  }

  // Subscription management
  if (showSubscription) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h2>Subscription</h2>
            <button onClick={() => setShowSubscription(false)}>✕</button>
          </div>
          <SubscriptionManager user={user} />
        </div>
      </div>
    );
  }

  // Main swipe screen
  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <Loader className="spinner" size={48} />
          <p>Loading beanz...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="swipe-container">
        {/* Header */}
        <div className="swipe-header">
          <h1>
            OnlyBeanz
          </h1>
        </div>

        {/* Card */}
        {beans.length === 0 ? (
          <div className="card empty-card">
            <Plus />
            <h2>No Beanz Yet!</h2>
            <p>Add your first beanz to start swiping</p>
            <button
              onClick={() => setShowAddBean(true)}
              className="button"
            >
              Add Your Beanz
            </button>
          </div>
        ) : currentBean ? (
          <div 
            className={`swipe-card ${ 
              swipeDirection === 'left' ? 'swipe-left' : 
              swipeDirection === 'right' ? 'swipe-right' : ''
            }`}
          >
            {/* Image */}
            <div className="card-image">
              <img 
                src={currentBean.image} 
                alt={currentBean.title}
              />
              <div className="overlay" />
              
              {/* Swipe indicators */}
              {swipeDirection === 'right' && (
                <div className="swipe-indicator right">
                  <div>
                    LOVE! 💚
                  </div>
                </div>
              )}
              {swipeDirection === 'left' && (
                <div className="swipe-indicator left">
                  <div>
                    NOPE! ❌
                  </div>
                </div>
              )}
              
              {/* Info overlay */}
                            <div className="card-info">
                              <h2>{currentBean.title}</h2>
                            </div>
                          </div>
              
                          {/* Details */}
                          <div className="card-details">

                          </div>          </div>
        ) : null}

        {beans.length > 0 && currentBean && (
          <div className="action-buttons">
            <button
              onClick={() => handleSwipe('left')}
              className="action-button dislike"
            >
              <X />
            </button>
            
            <button
              onClick={() => handleSwipe('right')}
              className="action-button like"
            >
              <Heart />
            </button>
          </div>
        )}

        <div className="main-actions">
          <div className="category-menu-container">
            <button
              onClick={() => setShowCategoryMenu(!showCategoryMenu)}
              title="Filter by category"
            >
              <Menu />
            </button>
            {showCategoryMenu && (
              <div className="category-menu">
                <button onClick={() => { setSelectedCategory(''); setShowCategoryMenu(false); }}>All Categories</button>
                {BEAN_CATEGORIES.map((category) => (
                  <button key={category} onClick={() => { setSelectedCategory(category); setShowCategoryMenu(false); }}>
                    {category}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => setShowLeaderboard(true)} title="Leaderboard">
            <Trophy />
          </button>
          
          <button onClick={() => setShowAddBean(true)} title="Add your beanz">
            <Plus />
          </button>
          
          <button onClick={() => setShowUserProfile(true)} title="My Profile">
            <User />
          </button>
          
          <button onClick={() => alert('COFFEE BUTTON WORKS!')} title="Support OnlyBeanz">
            COFFEE
          </button>
          
          <button onClick={handleLogout} title="Logout">
            <LogOut />
          </button>
        </div>

        <button 
          onClick={() => alert('COFFEE BUTTON WORKS!')} 
          style={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
            color: 'white',
            padding: '15px 30px',
            fontSize: '18px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            margin: '10px auto',
            display: 'block',
            fontWeight: 'bold',
            boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
            zIndex: '999',
            position: 'relative'
          }}
        >
          ☕ SUPPORT ONLYBEANZ
        </button>
        <p className="status-text">
          {beans.length - currentIndex} bean{beans.length - currentIndex !== 1 ? 'z' : ''} available
        </p>
      </div>
      <SideLeaderboard selectedCategory={selectedCategory} />
      <PWAInstallPrompt />
    </div>
  );
};

export default OnlyBeanzApp;
